import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { getNumberValueSeparatedByCommas } from '@/Utils/utils';
import { getAggregationOptionsByFieldType } from '../../../Utils/getAggreagationOptionsByFieldType';
import { addMixpanelEvent } from '@/redux/reducers/sharedSlice';
import { datasetSliceName, spreadsheetSliceName } from '@/redux/constants';
import { AGGREGATION_ERROR } from '@/Utils/constants';

import Icon from '../../../Components/UI/Icon/Icon';
import Tooltip from '@/Components/UI/Tooltip/Tooltip';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/Components/UI/Shadcn/DropdownMenu';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';

const optionsWithPercent = ['Percent Empty', 'Percent Filled', 'Percent Unique'];
const optionsWithCurrencySign = [
  'Mode',
  'Last Value',
  'First Value',
  'Range',
  'Max',
  'Min',
  'Median',
  'Average',
  'Sum',
];
const defaultAggregationOption = {
  label: '',
  value: '',
  aggValue: '',
};

const FunctionSelect = ({ agGrid, column, isCellAggSelector, value, valueFormatted }) => {
  const { isFileShared, WithinQuota, OverRowQuota, fileSize } = useSelector((state) => {
    const currentFile = state[datasetSliceName]?.currentFile || {};
    return {
      isFileShared: currentFile?.isFileShared,
      WithinQuota: currentFile?.WithinQuota,
      OverRowQuota: currentFile?.OverRowQuota,
      fileSize: currentFile?.metadata?.FileSize,
    };
  }, shallowEqual);

  const {
    aggregationEndpointValues,
    clientState,
    isAggregationEndpointValuesLoading,
    isDeletingRows,
    isDeletingColumns,
    isInsertingNewColumn,
  } = useSelector((state) => state[spreadsheetSliceName]);

  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const isAggregationLoadingAndDisable =
    isAggregationEndpointValuesLoading ||
    isDeletingRows ||
    isDeletingColumns ||
    isInsertingNewColumn;

  const [selectedAggregationOption, setSelectedAggregationOption] =
    useState(defaultAggregationOption);
  const aggregationOptionRef = useRef(null);

  const { colDef, aggFunc } = column;

  const isApplyFormattedValue = optionsWithCurrencySign.includes(selectedAggregationOption.label);

  const aggCellValue = isApplyFormattedValue ? valueFormatted : value;
  const isAggregationsPrevented = (!WithinQuota || OverRowQuota) && !isFileShared && fileSize;
  const options = isAggregationsPrevented ? [] : getAggregationOptionsByFieldType(colDef.fieldType);
  const isGroupedData = agGrid.api.getRowGroupColumns().length > 0;

  useEffect(() => {
    if (isAggregationsPrevented) return setSelectedAggregationOption(defaultAggregationOption);

    const aggFuncOption = options.find((el) => el.value === aggFunc);
    if (aggFunc) {
      const { aggFunc: aggregationName } =
        clientState.columnState?.find((el) => el.colId === colDef.field) || {};
      const { value: aggregationValue } =
        aggregationEndpointValues?.find((el) => el.column === colDef.field) || {};
      if (isGroupedData) {
        return setSelectedAggregationOption({
          ...aggFuncOption,
          aggValue: aggCellValue ?? aggregationValue,
        });
      } else {
        const displayValue = aggregationValue !== undefined ? aggregationValue : '';
        if (!aggregationName && !aggregationValue) {
          return setSelectedAggregationOption(defaultAggregationOption);
        }
        setSelectedAggregationOption({
          label: aggFuncOption?.label,
          value: aggregationName,
          aggValue: displayValue,
        });
      }
    }
  }, [
    agGrid.api,
    aggCellValue,
    aggFunc,
    column,
    isGroupedData,
    options,
    aggregationEndpointValues,
  ]);

  useEffect(() => {
    if (
      aggregationOptionRef.current &&
      aggregationOptionRef.current.scrollWidth > aggregationOptionRef.current.offsetWidth
    ) {
      const widthToAdd = column.colDef.minWidth + aggregationOptionRef.current.scrollWidth;
      agGrid.api.setColumnWidth(column.colId, widthToAdd);
    }
  }, [agGrid.api, aggregationOptionRef, column, selectedAggregationOption]);

  const ungroupedSelectChange = async (option) => {
    if (option.label === 'None') {
      agGrid.api.removeValueColumn(colDef.field);
      agGrid.api.setColumnAggFunc(colDef.field, '');
      return setSelectedAggregationOption(defaultAggregationOption);
    }
    agGrid.api.addValueColumn(colDef.field);
    agGrid.api.setColumnAggFunc(colDef.field, option.value);
    setSelectedAggregationOption({
      ...option,
      aggValue: aggregationEndpointValues.find((el) => el.column === colDef.field)?.value,
    });
  };

  const groupedSelectChange = (option) => {
    if (option.label === 'None') {
      agGrid.api.removeValueColumn(colDef.field);
      agGrid.api.setColumnAggFunc(colDef.field, '');
      setSelectedAggregationOption(defaultAggregationOption);
    } else {
      agGrid.api.addValueColumn(colDef.field);
      agGrid.api.setColumnAggFunc(colDef.field, option.value);
      setSelectedAggregationOption({ ...option, aggValue: aggCellValue });
    }
  };

  const trackMixpanelEvent = () => {
    dispatch(
      addMixpanelEvent({
        eventName: `Aggregation (${isGroupedData ? 'Grouped' : 'Ungrouped'})`,
        eventProps: {},
        userIncrementName: '# of aggregations used',
      })
    );
  };

  const getIsAggregationApplied = (selectedAggregationOption) => {
    const { aggValue, value } = selectedAggregationOption;
    return (
      aggValue ||
      aggValue === 0 ||
      (value && aggValue === '') ||
      (value && aggValue === 0) ||
      (value && aggValue === false)
    );
  };

  const isBoolean = (value) => {
    return typeof value === 'boolean';
  };

  const getUngroupedAggStyles = () => {
    return `not-agg-func-cell-select text-[10px] h-full flex items-center pl-[6px] ${
      getIsAggregationApplied(selectedAggregationOption) ? '!bg-[#DAD8F6] ' : ''
    }`;
  };

  const getGroupedAggStyles = () => {
    return 'aggregation-function-select-group agg-func-cell-select font-bold';
  };

  const valueWithoutPercent = optionsWithPercent.includes(selectedAggregationOption.label)
    ? selectedAggregationOption.label.split('Percent ')[1]
    : selectedAggregationOption.label;

  const isPercentageOption =
    selectedAggregationOption.aggValue !== undefined &&
    optionsWithPercent.includes(selectedAggregationOption.label);

  const isEmptyValue =
    selectedAggregationOption.aggValue === '' || selectedAggregationOption.aggValue === 0;

  const isUndefinedGroupedData = isGroupedData && selectedAggregationOption.aggValue === undefined;

  const aggValue = isPercentageOption ? (
    <span>{selectedAggregationOption.aggValue}&#37;</span>
  ) : isEmptyValue ? (
    selectedAggregationOption.aggValue
  ) : isUndefinedGroupedData ? (
    ''
  ) : isBoolean(selectedAggregationOption.aggValue) ? (
    selectedAggregationOption.aggValue.toString()
  ) : (
    getNumberValueSeparatedByCommas(selectedAggregationOption.aggValue)
  );

  const getFormattedValueText = (valueWithoutPercent, isGroupedData, selectedAggregationOption) => {
    if (
      valueWithoutPercent === '' ||
      (isGroupedData && selectedAggregationOption.aggValue === undefined)
    ) {
      return '';
    }
    return `${valueWithoutPercent}`;
  };

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open && isAggregationsPrevented) {
          showPreviewModeUpsellModal();
        }
      }}
    >
      <div
        className={`aggregation-function-select ${
          isCellAggSelector ? getGroupedAggStyles() : getUngroupedAggStyles()
        }`}
      >
        <div className='relative flex items-center gap-2 aggregation-function-name'>
          <DropdownMenuTrigger asChild>
            <div className='flex items-center gap-3 ml-2'>
              {isCellAggSelector &&
              selectedAggregationOption.value === 'none' ? null : isCellAggSelector ? (
                <Icon
                  name='caret-down-regular'
                  size={10}
                  alt='chevron-down'
                  className='text-black'
                />
              ) : (
                <Icon name='caret-down-regular' size={10} alt='chevron-down' />
              )}
              <span
                className={`${
                  isAggregationLoadingAndDisable
                    ? 'text-ui-helper font-overpass'
                    : 'text-ui font-overpass'
                }`}
                disabled={isAggregationLoadingAndDisable}
                data-cy='aggregation-function-name-label'
              >
                {getFormattedValueText(
                  valueWithoutPercent,
                  isGroupedData,
                  selectedAggregationOption
                )}
              </span>
            </div>
          </DropdownMenuTrigger>
          {selectedAggregationOption.aggValue === AGGREGATION_ERROR ? (
            <Tooltip text='Failed to calculate aggregation. Please try again later.' side='right'>
              <Icon name='smiley-sad' className='mr-1' size={16} />
            </Tooltip>
          ) : (
            <span
              ref={aggregationOptionRef}
              className='aggregation-function-value'
              data-cy='aggregation-function-value'
            >
              {selectedAggregationOption.aggValue !== '' ? aggValue : ''}
            </span>
          )}
        </div>
      </div>

      {!isAggregationsPrevented && (
        <DropdownMenuContent className='w-[175px] overflow-y-auto max-h-60' align='start'>
          <div data-cy='aggregation-function-select'>
            {options.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onSelect={() => {
                  trackMixpanelEvent(isGroupedData);
                  isGroupedData ? groupedSelectChange(option) : ungroupedSelectChange(option);
                }}
                className='!text-xs !text-ui-secondary hover:bg-gray-100 cursor-pointer h-8'
              >
                <div data-cy='aggregation-function-select-item'>{option.label}</div>
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
};

export default FunctionSelect;
